import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnInit,
  Optional,
  Self,
  SimpleChanges,
} from '@angular/core';
import { NgControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldControl, MatFormField, MatLabel, MatSuffix, MatError, MatHint } from '@angular/material/form-field';
import { LookupModel } from '../../api/models/lookup-model';
import { MatBaseInputComponent } from '../mat-base-input.component';
import { MatFormFieldFix } from '../mat-form-field/mat-form-field-fix';
import { NgClass, NgIf, NgFor } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/core';

@Component({
    selector: 'app-mat-simple-select',
    providers: [{ provide: MatFormFieldControl, useExisting: MatSimpleSelectComponent }],
    template: `
    <mat-form-field
      class="regular-form-field"
      [ngClass]="{ inline: inlineLabel }"
      [appearance]="appearance"
      [hideRequiredMarker]="hideRequiredMarker"
      (focusin)="onFocusIn($event)"
      (focusout)="onFocusOut($event)"
    >
      <mat-label *ngIf="!inlineLabel">
        {{ label }}
        <mat-icon *ngIf="!!labelExplanation" matTooltip="{{ labelExplanation }}">info_outline</mat-icon>
      </mat-label>
      <mat-select
        [hideSingleSelectionIndicator]="true"
        [formControl]="formControl"
        [id]="_id"
        (valueChange)="onChange($event)"
        (closed)="onClose()"
        [placeholder]="placeholder ? placeholder : inlineLabel ? label : ''"
        [multiple]="multiple"
      >
        <ng-container *ngIf="searchEnabled">
          <div style="padding: 10px">
            <input
              class="filterInput"
              type="text"
              [placeholder]="searchPlaceholder"
              [(ngModel)]="searchTerm"
              (ngModelChange)="filterItems()"
            />
          </div>

          <mat-option
            *ngIf="allowCustomOption && searchTerm?.length"
            [value]="searchTerm"
            [disabled]="!isCustomOptionValid"
          >
            {{ searchTerm }} {{ !isCustomOptionValid ? '(invalid)' : '' }}
          </mat-option>

          <mat-option *ngIf="!filteredItems.length && !allowCustomOption" [disabled]="true">
            No available options
          </mat-option>
        </ng-container>

        <mat-option *ngFor="let item of optionItems" [value]="item.id" [disabled]="isItemDisabled(item)">
          {{ item.text }}
        </mat-option>

        <mat-option *ngIf="searchEnabled" [hidden]="true" [disabled]="true" [value]="currentlySelected?.id">
          {{ currentlySelected?.text }}
        </mat-option>
      </mat-select>
      <mat-icon matSuffix class="explanation" *ngIf="!!explanation" matTooltip="{{ explanation }}"
        >info_outline
      </mat-icon>
      <mat-error *ngIf="formControl?.invalid">{{ firstError() }}</mat-error>
      <mat-hint align="start">{{ infoText }}</mat-hint>
    </mat-form-field>
  `,
    styleUrls: ['mat-simple-select.component.scss'],
    standalone: true,
    imports: [
        MatFormField,
        MatFormFieldFix,
        NgClass,
        NgIf,
        MatLabel,
        MatIcon,
        MatTooltip,
        MatSelect,
        FormsModule,
        ReactiveFormsModule,
        MatOption,
        NgFor,
        MatSuffix,
        MatError,
        MatHint,
    ],
})
export class MatSimpleSelectComponent extends MatBaseInputComponent implements OnInit, OnChanges, AfterViewInit {
  @Input() selectItems: LookupModel[] = [];
  @Input() disabledItems: LookupModel[] = [];
  @Input() multiple = false;
  @Input() hideRequiredMarker = false;
  @Input() searchEnabled = false;
  @Input() allowCustomOption = false;
  @Input() searchPlaceholder = 'Search...';
  @Input() customOptionRule?: RegExp;

  public searchTerm: string = '';
  public filteredItems: LookupModel[] = [];
  public isCustomOptionValid: boolean = true;

  constructor(
    @Optional() @Self() public ngControl: NgControl,
    public elementRef: ElementRef<HTMLElement>,
  ) {
    super(ngControl, elementRef);
  }

  get optionItems(): LookupModel[] {
    return this.searchEnabled ? this.filteredItems : this.selectItems;
  }

  get currentlySelected(): LookupModel {
    const formValue = this.formControl.getRawValue();

    return this.selectItems.find(item => item.id === formValue) || { id: formValue, text: formValue };
  }

  isItemDisabled(item: LookupModel): boolean {
    const disabledItem = this.disabledItems.find(x => x.id === item.id);
    return !!disabledItem || item.text.indexOf('invalid') > -1;
  }

  ngOnInit(): void {
    if (this.searchEnabled) {
      this.filteredItems = [...this.selectItems];
    }

    this.formControl.markAsTouched();
  }

  ngOnChanges(changes: SimpleChanges): void {
    super.ngOnChanges(changes);

    if (this.searchEnabled && changes['selectItems']) {
      this.resetFilter();
    }
  }

  ngAfterViewInit(): void {
    this.removeAriaOwns();
  }

  public filterItems(): void {
    if (this.searchTerm) {
      if (this.allowCustomOption && this.customOptionRule) {
        this.isCustomOptionValid = this.isSearchTermValid();
      }

      this.filteredItems = this.selectItems.filter(item =>
        item.text.toLowerCase().includes(this.searchTerm.toLowerCase()),
      );
    } else {
      this.filteredItems = [...this.selectItems];
    }
  }

  public onClose(): void {
    if (this.searchEnabled) {
      this.resetFilter();
    }
  }

  public isSearchTermValid(): boolean {
    return this.customOptionRule.test(this.searchTerm);
  }

  private resetFilter(): void {
    this.searchTerm = '';
    this.filterItems();
  }

  private removeAriaOwns(): void {
    //console.log(`removeAriaOwns`)
    // Get the parent element of mat-select
    const parentElement = this.elementRef.nativeElement.parentElement;

    parentElement.querySelectorAll('[aria-owns]').forEach(element => {
      element.removeAttribute('aria-owns');
    });
    parentElement.querySelectorAll('[aria-labelledby]').forEach(element => {
      element.removeAttribute('aria-labelledby');
    });
  }
}
